/**
 * useStaffNames — resolve a stamped actor (an email like "seths@…" or an auth
 * user_id) to a real staff name ("Seth"), so audited actions and "created by"
 * lines read as people, not login handles. Source: app_user (email + user_id +
 * empno) → mirror_employee (firstname / empname), the same chain useCurrentUser
 * uses. Cached for the session.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { firstNameFrom } from "@/lib/currentUser";

export interface StaffName {
  first: string;
  full: string;
}

export interface StaffNameMaps {
  byEmail: Map<string, StaffName>;
  byUid: Map<string, StaffName>;
}

export function useStaffNames() {
  return useQuery({
    queryKey: ["staff_names"],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<StaffNameMaps> => {
      const { data: users } = await supabase
        .from("app_user")
        .select("user_id, email, empno");
      const rows = (users ?? []) as { user_id: string | null; email: string | null; empno: string | null }[];
      const empnos = [...new Set(rows.map((u) => u.empno).filter(Boolean) as string[])];
      const { data: emps } = empnos.length
        ? await supabase.from("mirror_employee").select("empno, firstname, empname").in("empno", empnos)
        : { data: [] as unknown[] };
      const empMap = new Map(
        ((emps ?? []) as { empno: string; firstname: string | null; empname: string | null }[]).map((e) => [e.empno, e]),
      );

      const byEmail = new Map<string, StaffName>();
      const byUid = new Map<string, StaffName>();
      for (const u of rows) {
        const e = u.empno ? empMap.get(u.empno) : null;
        const full = (e?.empname && e.empname.trim()) || u.email || "";
        const first = (e?.firstname && e.firstname.trim()) || firstNameFrom(null, u.email) || full;
        const sn: StaffName = { first, full };
        if (u.email) byEmail.set(u.email.toLowerCase(), sn);
        if (u.user_id) byUid.set(u.user_id, sn);
      }
      return { byEmail, byUid };
    },
  });
}

/**
 * Friendly first name for a stamped actor. `actor` may be an email or a stored
 * friendly name; `uid` is the optional auth id (audit rows carry it). Falls back
 * to a tidied email handle, then the raw value, then "system".
 */
export function staffFirst(
  maps: StaffNameMaps | undefined,
  actor: string | null | undefined,
  uid?: string | null,
): string {
  if (uid && maps?.byUid.has(uid)) return maps.byUid.get(uid)!.first;
  if (!actor) return "system";
  if (actor === "service" || actor === "service_role" || actor === "system") return "system";
  if (actor.includes("@")) {
    const hit = maps?.byEmail.get(actor.toLowerCase());
    return hit?.first ?? firstNameFrom(null, actor) ?? actor;
  }
  return actor; // already a friendly name (e.g. event actors stamped as first names)
}
